import { create } from "zustand";
import { devtools } from "zustand/middleware";
import settingsAPI from "../../data/remote/settings";
import { message } from "antd";

type UserLimitsData = {
    enabled: boolean;
};

type PlaceAutoApproveData = {
    auto_approve: boolean;
};

interface SettingsState {
    userLimitsData: UserLimitsData | null;
    placeAutoApproveData: PlaceAutoApproveData | null;
    isLoading: boolean;
    isSaving: boolean;
    isSavingPlaceConfig: boolean;
    getUserLimitsStatus: () => Promise<void>;
    updateUserLimits: (enabled: boolean) => Promise<{ success: boolean }>;
    getPlaceAutoApproveConfig: () => Promise<void>;
    togglePlaceAutoApprove: (enabled: boolean) => Promise<{ success: boolean }>;
}

const useSettingsStore = create<SettingsState>()(
    devtools((set) => ({
        userLimitsData: null,
        placeAutoApproveData: null,
        isLoading: false,
        isSaving: false,
        isSavingPlaceConfig: false,
        getUserLimitsStatus: async () => {
            set({ isLoading: true });
            try {
                const response = await settingsAPI.getUserLimitsStatus();
                if (response.success) {
                    set({ userLimitsData: response.data });
                } else {
                    message.error(response.message || "Failed to fetch user limits status");
                }
            } catch (error: any) {
                message.error(error.response?.data?.message || "Failed to fetch user limits status");
            } finally {
                set({ isLoading: false });
            }
        },
        updateUserLimits: async (enabled: boolean) => {
            set({ isSaving: true });
            try {
                const response = await settingsAPI.updateUserLimits(enabled);
                if (response.success) {
                    set({ userLimitsData: response.data });
                    message.success(enabled ? "User limits enabled successfully" : "User limits disabled successfully");
                    return { success: true };
                } else {
                    message.error(response.message || "Failed to update user limits");
                    return { success: false };
                }
            } catch (error: any) {
                message.error(error.response?.data?.message || "Failed to update user limits");
                return { success: false };
            } finally {
                set({ isSaving: false });
            }
        },
        getPlaceAutoApproveConfig: async () => {
            set({ isLoading: true });
            try {
                const response = await settingsAPI.getPlaceAutoApproveConfig();
                if (response.success) {
                    set({ placeAutoApproveData: response.data });
                } else {
                    message.error(response.message || "Failed to fetch place auto-approve config");
                }
            } catch (error: any) {
                message.error(error.response?.data?.message || "Failed to fetch place auto-approve config");
            } finally {
                set({ isLoading: false });
            }
        },
        togglePlaceAutoApprove: async (enabled: boolean) => {
            set({ isSavingPlaceConfig: true });
            try {
                const response = await settingsAPI.togglePlaceAutoApprove(enabled);
                if (response.success) {
                    set({ placeAutoApproveData: response.data });
                    message.success(
                        enabled
                            ? "Place auto-approve enabled successfully"
                            : "Place auto-approve disabled successfully"
                    );
                    return { success: true };
                } else {
                    message.error(response.message || "Failed to update place auto-approve");
                    return { success: false };
                }
            } catch (error: any) {
                message.error(error.response?.data?.message || "Failed to update place auto-approve");
                return { success: false };
            } finally {
                set({ isSavingPlaceConfig: false });
            }
        },
    }))
);

export default useSettingsStore;
